/**
 * Featured News Section
 * Large highlighted card for the latest/featured content
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, MapPin, Clock } from 'lucide-react';
import banner1 from '../../../assets/images/banner1.png';

const FeaturedNews = ({ item }) => {
  if (!item) return null;

  const isEvent = item.type === 'event';
  const getCategoryColor = (cat) => {
    const palette = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-orange-100 text-orange-700',
      'bg-purple-100 text-purple-700',
      'bg-red-100 text-red-700',
      'bg-teal-100 text-teal-700',
      'bg-indigo-100 text-indigo-700',
      'bg-pink-100 text-pink-700',
      'bg-cyan-100 text-cyan-700',
      'bg-amber-100 text-amber-700',
      'bg-lime-100 text-lime-700',
      'bg-sky-100 text-sky-700',
    ];
    if (!cat) return 'bg-gray-100 text-gray-700';
    let hash = 0;
    for (let i = 0; i < cat.length; i++) {
      hash = cat.charCodeAt(i) + ((hash << 5) - hash);
    }
    return palette[Math.abs(hash) % palette.length];
  };
  const badgeClass = getCategoryColor(item.category);

  return (
    <section className="bg-gray-50 py-8 md:py-10">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Section Label */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-1 h-5 bg-[#ca9a37] rounded-full" />
          <h2 className="text-lg font-bold text-[#2f3951]">Features</h2>
        </div>

        {/* Featured Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-xl">
          <div className="grid md:grid-cols-2">
            {/* Left — Image */}
            <div className="relative h-56 md:h-auto min-h-[280px] overflow-hidden">
              <img
                src={item.image || banner1}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${badgeClass}`}>
                  {item.category}
                </span>
              </div>
              {isEvent && (
                <div className="absolute bottom-4 left-4">
                  <div className="bg-[#ca9a37] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md">
                    <Calendar className="w-4 h-4 inline mr-1.5" />
                    {item.eventDate}
                  </div>
                </div>
              )}
            </div>

            {/* Right — Content */}
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {item.date}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {item.category}
                </span>
              </div>

              <h3 className="text-xl md:text-2xl font-bold text-[#2f3951] mb-3 leading-snug">
                {item.title}
              </h3>

              <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                {item.summary}
              </p>

              {isEvent && item.location && (
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#ca9a37]" />
                    {item.location}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#ca9a37]" />
                    {item.time}
                  </span>
                </div>
              )}

              <Link
                to={`/news-events/${item.id}`}
                className="inline-flex items-center text-[#ca9a37] font-semibold text-sm hover:text-[#b88a2f] transition-colors group"
              >
                {isEvent ? 'View Event Details' : 'Read Full Article'}
                <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedNews;
